import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { LeadService } from '../shared/service/leads.service';
import { CraditService } from '../../credit/shared/services/cradit.service';
import { Setup } from '../../setup/shared/service/setup';
import { MODULE_SEQ } from '../../../core/shared/models/picklist.model';
import { CommonModule } from '@angular/common';
import { Table } from '../../shared/table/table';
import { BackButton } from '../../shared/buttons/back-button';
import {
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import {
  cleanBase64,
  getFileInfo,
  getFileNameWithoutExtension,
} from '../shared/utils/document-file.util';

@Component({
  selector: 'app-document-history',
  imports: [CommonModule, Table, BackButton,TranslatePipe],
  templateUrl: './document-history.html',
  styleUrl: './document-history.css',
})
export class DocumentHistory implements OnInit, OnChanges {
  /**
   * Embedded in a popup (the Credit Proposal Documents tab): the entity,
   * document type and entity type come from these inputs instead of the
   * route, and the page header (title + Back) is left to the popup. The
   * routed /document-history page never sets them and works as before.
   */
  @Input() inPopup = false;
  @Input() historyEntitySeq: number | null = null;
  @Input() historyDocType: number | null = null;
  @Input() historyEntyTyp: number | null = null;

  leadSeq: number | null = null;
  docType: number | null = null;
  entyTyp: number | null = null;
  isLoading = false;

  /**
   * Set by the Facility Offer Letter documents screens (`?source=foLtr`),
   * whose history lives in its own table — those hit
   * GET /crdt-prpsl/fo-ltr/get-fo-ltr-doc-hstry/{crdtFoLtrSeq}/{docSeq}.
   * Every other screen keeps the leads history endpoint.
   */
  isFoLtrHistory = false;

  /* ================= DATA ================= */
  documents: any[] = [];

  /* ================= TABLE ================= */
columns: TableColumn[] = [];
buildColumns(): void {
  this.columns = [
    {
      key: 'docNm',
      label: this.translate.instant('DOCUMENT_HISTORY.DOCUMENT_NAME'),
      type: 'link',
      onClick: (row: any) => this.downloadFile(row),
    },
    {
      key: 'docTypLabel',
      label: this.translate.instant('DOCUMENT_HISTORY.DOCUMENT_TYPE'),
    },
    // Only the Monitoring Report history carries a visit date — every other
    // screen's history (leads, onboarding, etc.) never sets docDt.
    ...(this.entyTyp === MODULE_SEQ.MONITORING_REPORT
      ? [{ key: 'visitDateLabel', label: 'DOCUMENT_HISTORY.VISIT_DATE' } as TableColumn]
      : []),
    // Monitoring Report keeps its "Document Update Date"; every other history
    // shows when each version was created (`crtdDt`).
    this.entyTyp === MODULE_SEQ.MONITORING_REPORT
      ? { key: 'updatedDate', label: 'DOCUMENT_HISTORY.DOCUMENT_UPDATE_DATE' }
      : { key: 'createdDate', label: this.translate.instant('DOCUMENT_HISTORY.CREATED_ON') },
    // Facility Offer Letter history only: get-fo-ltr-doc-hstry carries `issuanceDt`
    // (shown as the Printed Date) and `uploadDt`.
    ...(this.isFoLtrHistory
      ? [
          { key: 'issuanceDt', label: 'DOCUMENT_HISTORY.PRINTED_DATE', type: 'date' } as TableColumn,
          { key: 'uploadDt', label: 'DOCUMENT_HISTORY.UPLOADED_DATE', type: 'date' } as TableColumn,
        ]
      : []),
  ];
}
  actions: TableAction[] = [
   
  ];

  constructor(
    private route: ActivatedRoute,
    private leadService: LeadService,
    private toastr: ToastrService,
     private translate: TranslateService,
    private creditService: CraditService,
    private setup: Setup,
  ) {}

  /* ================= DOCUMENT TYPE LABELS ================= */
  /**
   * The fo-ltr history returns the document type as a bare seq, so the labels
   * are resolved the same way the documents list does. The leads history
   * already carries `docTypNm`, so it never needs this lookup.
   */
  private docTypeLabels = new Map<number, string>();

  private rawDocuments: any[] = [];

  private loadDocTypeLabels(): void {
    if (!this.isFoLtrHistory || !this.entyTyp || this.docTypeLabels.size) {
      return;
    }

    this.setup.getAllDocumentByCategry(this.entyTyp).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res?.data || [];

        list.forEach((item: any) => {
          this.docTypeLabels.set(Number(item.docSeq), item.docNm);
        });

        this.documents = this.rawDocuments.map((doc: any) => this.mapHistoryRow(doc));
      },
      error: () => {
        // Labels are cosmetic — the history still renders without them.
      },
    });
  }

  /* ================= INIT ================= */
ngOnInit(): void {
  this.buildColumns();

  this.translate.onLangChange.subscribe(() => {
    this.buildColumns();
  });

  if (this.inPopup) {
    // Inputs drive the load (ngOnChanges), not the route.
    return;
  }

  this.route.paramMap.subscribe((params) => {
    const id = params.get('id');
    this.leadSeq = id ? +id : null;
    this.tryLoad();
  });

  this.route.queryParamMap.subscribe((q) => {
    const t = q.get('docType');
    const e = q.get('entyTyp');

    this.docType = t ? +t : null;
    this.entyTyp = e ? +e : null;
    this.isFoLtrHistory = q.get('source') === 'foLtr';

    this.buildColumns();
    this.tryLoad();
  });
}

  ngOnChanges(): void {
    if (!this.inPopup) {
      return;
    }

    this.leadSeq = this.historyEntitySeq;
    this.docType = this.historyDocType;
    this.entyTyp = this.historyEntyTyp;

    this.buildColumns();
    this.tryLoad();
  }

  // dono values aane ke baad load
  private tryLoad(): void {
    if (this.leadSeq && this.docType !== null && this.entyTyp !== null) {
      this.loadHistory();
    }
  }

  /* ================= LOAD ================= */
  loadHistory(): void {
    if (
      this.leadSeq === null ||
      this.docType === null ||
      this.entyTyp === null
    ) {
      return;
    }

    this.isLoading = true;

    const request$ = this.isFoLtrHistory
      ? this.creditService.getFoLtrDocHistory(this.leadSeq, this.docType)
      : this.leadService.getLeadDocHistory(this.leadSeq, this.entyTyp, this.docType);

    request$.subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res?.data || [];

        this.rawDocuments = list;

        this.documents = list.map((doc: any) => this.mapHistoryRow(doc));

        this.loadDocTypeLabels();

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Document history error:', err);
        this.toastr.error(this.translate.instant('DOCUMENT_HISTORY.FAILED_TO_LOAD'));
        this.isLoading = false;
      },
    });
  }

  /**
   * The Facility Offer Letter history names its rows the way the rest of the
   * fo-ltr APIs do — the row's own seq is `crdtFoLtrDocSeq` and the document
   * *type* is `docSeq` — where the leads history calls them `entyDocSeq` and
   * `docTyp`.
   */
  private mapHistoryRow(doc: any): any {
    const docSeq = this.isFoLtrHistory ? doc.crdtFoLtrDocSeq : doc.entyDocSeq;

    const docTyp = this.isFoLtrHistory ? doc.docSeq : doc.docTyp;

    return {
      docSeq,
      docNm: doc.docNm,
      docTyp,
      entyTyp: doc.entyTyp ?? this.entyTyp,
      docTypLabel: doc.docTypNm || this.docTypeLabels.get(Number(docTyp)) || docTyp,
      docDesc: doc.docDesc,
      // fo-ltr history rows that were never edited only carry `crtdDt`.
      updatedDate: this.formatTimestamp(doc.lastUpdDt ?? doc.crtdDt),
      createdDate: this.formatTimestamp(doc.crtdDt),
      issuanceDt: doc.issuanceDt ?? null,
      uploadDt: doc.uploadDt ?? null,
      visitDateLabel: doc.docDt ? new Date(doc.docDt).toLocaleDateString() : '-',
      docImg: doc.docImg,
    };
  }

  /**
   * Shows the timestamp exactly as the API sent it.
   *
   * The history endpoint stamps its times with a UTC designator (`...Z` /
   * `+00:00`) even though the value is already the server's local time, so
   * `new Date(...)` shifted it by the browser's offset — the history read five
   * hours ahead of the same row on the documents list, whose endpoint sends the
   * same instant without a designator. Dropping the designator makes both
   * screens agree.
   */
  private formatTimestamp(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const parsed =
      typeof value === 'number'
        ? new Date(value)
        : new Date(String(value).replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, ''));

    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
  }

  /* ================= DOWNLOAD ================= */
  downloadFile(row: any): void {
    if (!row?.docImg) {
      this.toastr.warning(this.translate.instant('DOCUMENT_HISTORY.NO_FILE_AVAILABLE'));
      return;
    }

    try {
      const base64Data = cleanBase64(row.docImg);

      if (!base64Data) {
        this.toastr.error(this.translate.instant('DOCUMENT_HISTORY.INVALID_DOCUMENT_DATA'));
        return;
      }

      // Same detection used by the Document component — reads far enough
      // into the file to tell a real .docx/.xlsx/.pptx apart from a plain
      // .zip, so the download always keeps the original upload extension.
      const { mimeType, extension } = getFileInfo(base64Data, row.docNm);

      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const fileName = `${getFileNameWithoutExtension(row.docNm || 'document')}.${extension}`;

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.toastr.success(this.translate.instant('DOCUMENT_HISTORY.DOWNLOAD_STARTED'));
    } catch (error) {
      console.error('Download failed:', error);
      this.toastr.error(this.translate.instant('DOCUMENT_HISTORY.FAILED_TO_DOWNLOAD_FILE'));
    }
  }
}